(function () {
  // Product comparison. Two custom elements sharing one localStorage-backed store:
  //
  //   <product-compare-bar>   the selection tray on collection pages
  //   <product-compare-table> the table on /pages/compare
  //
  // Liquid cannot read a query string, so the compare page cannot be rendered
  // server-side from ?products=. Each column is fetched instead through the
  // Section Rendering API — /products/<handle>?sections=compare-column — which is
  // the same trick sections/product.liquid uses for pickup availability.

  const MAX = 3;
  const SELECTION_KEY = 'product-compare-selection';
  const MODE_KEY = 'product-compare-mode';
  const ORIGIN_KEY = 'product-compare-origin';
  const COMPARE_URL = '/pages/compare';

  // ── Store ────────────────────────────────────────────────────────────────
  // Entries are {handle, title, url, image} so the bar can draw a chip without a
  // request — the card checkbox carries all four. Private browsing and disabled
  // storage throw on access, so every touch is guarded and simply degrades to
  // not persisting.

  function readSelection() {
    try {
      const raw = localStorage.getItem(SELECTION_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => item && typeof item.handle === 'string').slice(0, MAX);
    } catch (e) {
      return [];
    }
  }

  function writeSelection(items) {
    try {
      localStorage.setItem(SELECTION_KEY, JSON.stringify(items));
    } catch (e) {
      /* storage unavailable — selection just won't survive the page */
    }
  }

  function readMode() {
    try {
      return localStorage.getItem(MODE_KEY) === 'on';
    } catch (e) {
      return false;
    }
  }

  function writeMode(on) {
    try {
      localStorage.setItem(MODE_KEY, on ? 'on' : 'off');
    } catch (e) {
      /* see writeSelection */
    }
  }

  // Where the shopper came from, so the compare page can offer a way back. The
  // compare page cannot work this out for itself — it is a plain Shopify page
  // with no relationship to the collection in Liquid.
  function readOrigin() {
    try {
      const raw = localStorage.getItem(ORIGIN_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      // Same-origin relative paths only — never navigate somewhere a stale or
      // tampered storage value points at.
      if (!parsed || typeof parsed.url !== 'string' || parsed.url.charAt(0) !== '/' || parsed.url.charAt(1) === '/') {
        return null;
      }
      return {
        url: parsed.url,
        title: typeof parsed.title === 'string' ? parsed.title : 'Back',
        // Plain handle characters only — it is interpolated into a fetch path.
        handle: /^[a-z0-9-]+$/i.test(parsed.handle || '') ? parsed.handle : null
      };
    } catch (e) {
      return null;
    }
  }

  function writeOrigin(origin) {
    try {
      localStorage.setItem(ORIGIN_KEY, JSON.stringify(origin));
    } catch (e) {
      /* see writeSelection */
    }
  }

  // ── Collection page ──────────────────────────────────────────────────────

  class ProductCompareBar extends HTMLElement {
    connectedCallback() {
      this._chips = this.querySelector('[data-compare-chips]');
      this._clearBtn = this.querySelector('[data-compare-clear]');
      this._goBtn = this.querySelector('[data-compare-go]');
      this._toggle = document.querySelector('[data-compare-toggle]');
      this._selection = readSelection();

      // Delegated so cards appended by assets/infinite-scroll.js are covered —
      // that file dispatches no event after appending, and binding per-card at
      // load would miss every later page.
      this._onChange = (e) => {
        const box = e.target.closest('[data-compare-checkbox]');
        if (box) this._onCheckboxChange(box);
      };
      document.addEventListener('change', this._onChange);

      this._onToggleChange = () => this._setMode(this._toggle.checked);
      if (this._toggle) this._toggle.addEventListener('change', this._onToggleChange);

      this._onClear = () => {
        this._selection = [];
        this._commit();
      };
      if (this._clearBtn) this._clearBtn.addEventListener('click', this._onClear);

      this._onGo = () => {
        if (this._selection.length < 2) return;
        // Refreshed here as well as in _commit: the shopper may have changed
        // the sort or filters since the last box was ticked.
        writeOrigin(this._origin());
        const handles = this._selection.map((item) => item.handle);
        window.location.href = COMPARE_URL + '?products=' + encodeURIComponent(handles.join(','));
      };
      if (this._goBtn) this._goBtn.addEventListener('click', this._onGo);

      this._onChipClick = (e) => {
        const btn = e.target.closest('[data-compare-remove]');
        if (!btn) return;
        this._remove(btn.getAttribute('data-compare-remove'));
      };
      if (this._chips) this._chips.addEventListener('click', this._onChipClick);

      // Cards arriving from infinite scroll start unchecked no matter what the
      // shopper picked earlier, so re-sync each batch as it lands.
      const grid = document.getElementById('collection-product-grid');
      if (grid) {
        this._observer = new MutationObserver(() => this._syncCheckboxes());
        this._observer.observe(grid, { childList: true });
      }

      this._setMode(readMode());
      this._syncCheckboxes();
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onChange);
      if (this._toggle) this._toggle.removeEventListener('change', this._onToggleChange);
      if (this._clearBtn) this._clearBtn.removeEventListener('click', this._onClear);
      if (this._goBtn) this._goBtn.removeEventListener('click', this._onGo);
      if (this._chips) this._chips.removeEventListener('click', this._onChipClick);
      if (this._observer) this._observer.disconnect();
    }

    _setMode(on) {
      this._modeOn = on;
      if (on) {
        document.documentElement.setAttribute('data-compare-mode', '');
      } else {
        document.documentElement.removeAttribute('data-compare-mode');
      }
      // Also set on restore from storage, where nothing clicked the box.
      if (this._toggle) this._toggle.checked = on;
      writeMode(on);
      this._render();
    }

    _onCheckboxChange(box) {
      if (box.checked) {
        if (this._selection.length >= MAX) {
          box.checked = false;
          return;
        }
        this._selection.push({
          handle: box.value,
          title: box.getAttribute('data-product-title') || '',
          url: box.getAttribute('data-product-url') || '',
          image: box.getAttribute('data-product-image') || ''
        });
      } else {
        this._selection = this._selection.filter((item) => item.handle !== box.value);
      }
      this._commit();
    }

    _remove(handle) {
      this._selection = this._selection.filter((item) => item.handle !== handle);
      this._commit();
    }

    // Where the shopper is standing, filters and sort included, so the crumb on
    // the compare page returns them to the exact grid they left.
    _origin() {
      return {
        url: window.location.pathname + window.location.search,
        title: this.getAttribute('data-collection-title') || document.title,
        // Lets the compare page offer the rest of the collection as swaps.
        handle: this.getAttribute('data-collection-handle') || ''
      };
    }

    _commit() {
      writeSelection(this._selection);
      // Recorded on every change, not just on Compare, because the guard in
      // layout/theme.liquid uses it to decide whether a selection still belongs
      // where the shopper has navigated to — and a selection that was never
      // taken through to the compare page needs answering for too.
      if (this._selection.length) writeOrigin(this._origin());
      this._syncCheckboxes();
      this._render();
    }

    _syncCheckboxes() {
      const handles = this._selection.map((item) => item.handle);
      const full = handles.length >= MAX;
      document.querySelectorAll('[data-compare-checkbox]').forEach((box) => {
        const selected = handles.indexOf(box.value) !== -1;
        box.checked = selected;
        // At the cap the remaining boxes go disabled rather than silently
        // refusing the click — the global :disabled style already dims them.
        box.disabled = full && !selected;
      });
    }

    _render() {
      const count = this._selection.length;
      this.hidden = !this._modeOn || count === 0;

      if (this._goBtn) this._goBtn.disabled = count < 2;

      if (!this._chips) return;
      this._chips.textContent = '';
      this._selection.forEach((item) => {
        this._chips.appendChild(this._buildChip(item));
      });
    }

    _buildChip(item) {
      // Built node by node rather than from an HTML string so a product title
      // containing markup cannot break out of the chip.
      const chip = document.createElement('span');
      chip.className = 'product-compare-chip';

      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        chip.appendChild(img);
      }

      const title = document.createElement('span');
      title.className = 'product-compare-chip__title';
      title.textContent = item.title || item.handle;
      chip.appendChild(title);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'product-compare-chip__remove';
      remove.setAttribute('data-compare-remove', item.handle);
      remove.setAttribute('aria-label', 'Remove ' + (item.title || item.handle) + ' from comparison');
      const icon = document.createElement('i');
      icon.className = 'ph ph-x';
      icon.setAttribute('aria-hidden', 'true');
      remove.appendChild(icon);
      chip.appendChild(remove);

      return chip;
    }
  }

  // ── Compare page ─────────────────────────────────────────────────────────

  class ProductCompareTable extends HTMLElement {
    connectedCallback() {
      this._labels = [...this.querySelectorAll('[data-spec-label]')].map((el) => el.textContent.trim());
      this._loading = this.querySelector('[data-compare-loading]');
      this._empty = this.querySelector('[data-compare-empty]');
      this._output = this.querySelector('[data-compare-output]');

      this._addOriginCrumb();

      const handles = this._handles();
      if (!handles.length) {
        this._showEmpty();
        return;
      }

      // Narrow screens get the table transposed — products down the left, specs
      // across the top — rather than the desktop arrangement, which is far too
      // wide for a phone. Both are real tables, so the orientation is chosen
      // here and the table rebuilt, not faked with CSS.
      this._mq = window.matchMedia('(width < 48rem)');

      Promise.all([Promise.all(handles.map((handle) => this._fetchColumn(handle))), this._loadSwapOptions()])
        .then((results) => {
          const columns = results[0];
          // Drop handles whose column failed alongside the column itself, so the
          // two stay index-aligned — a swap addresses a column by its index.
          this._columns = [];
          this._handleList = [];
          columns.forEach((column, i) => {
            if (!column) return;
            this._columns.push(column);
            this._handleList.push(handles[i]);
          });
          if (!this._columns.length) {
            this._showEmpty();
            return;
          }
          this._options = results[1];
          this._draw();
          this._onBreakpoint = () => this._draw();
          this._mq.addEventListener('change', this._onBreakpoint);
          if (this._loading) this._loading.hidden = true;
        })
        .catch(() => this._showEmpty());
    }

    // The products offered as swaps are the collection the shopper came from.
    // Nothing to offer on a shared link opened cold — the headers stay plain
    // links in that case rather than showing an empty dropdown.
    _loadSwapOptions() {
      const origin = readOrigin();
      if (!origin || !origin.handle) return Promise.resolve(null);
      return fetch('/collections/' + origin.handle + '/products.json?limit=250')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data || !Array.isArray(data.products) || !data.products.length) return null;
          return data.products.map((p) => ({ handle: p.handle, title: p.title }));
        })
        .catch(() => null);
    }

    // Replaces one column in place. The URL is kept in step so the comparison
    // stays shareable and survives a reload.
    _swap(col, handle) {
      if (!handle || this._handleList.indexOf(handle) !== -1) return;
      this._fetchColumn(handle).then((column) => {
        if (!column) return;
        this._columns[col] = column;
        this._handleList[col] = handle;
        const url = new URL(window.location.href);
        url.searchParams.set('products', this._handleList.join(','));
        window.history.replaceState({}, '', url);
        this._focusColumn = col;
        this._draw(true);
      });
    }

    disconnectedCallback() {
      if (this._mq && this._onBreakpoint) this._mq.removeEventListener('change', this._onBreakpoint);
    }

    // Only rebuilds when the orientation actually changes, so an ordinary resize
    // within one breakpoint costs nothing. A swap passes force, having changed
    // the data rather than the layout.
    _draw(force) {
      const byProduct = this._mq.matches;
      if (!force && this._drawnByProduct === byProduct) return;
      this._drawnByProduct = byProduct;
      this._output.textContent = '';
      this._output.appendChild(this._buildTable(this._columns, byProduct));
      this._markOverflow();

      // Redrawing throws away the <select> that was just used, so put focus back
      // where the shopper left it.
      if (this._focusColumn != null) {
        const selects = this._output.querySelectorAll('[data-compare-swap]');
        const restored = selects[this._focusColumn];
        if (restored) restored.focus();
        this._focusColumn = null;
      }
    }

    // The fade over a clipped title is applied by class, not unconditionally, so
    // a title that fits is never dimmed at its last word. Only measurable once
    // the table is in the document — one line across the top on desktop, up to
    // two down the product column on mobile, so both axes are worth checking.
    _markOverflow() {
      this._output.querySelectorAll('.compare-select-label').forEach((label) => {
        const clipped = label.scrollWidth > label.clientWidth + 1 || label.scrollHeight > label.clientHeight + 1;
        label.classList.toggle('is-clamped', clipped);
      });
    }

    // Extends the server-rendered "Home" trail with the collection the shopper
    // left, matching the markup snippets/breadcrumbs.liquid emits for a parent
    // crumb. Those classes all appear in that snippet, so Tailwind has already
    // compiled them — utilities invented here would be purged.
    _addOriginCrumb() {
      const origin = readOrigin();
      const nav = document.querySelector('.breadcrumbs');
      if (!origin || !nav) return;

      const separator = document.createElement('span');
      separator.className = 'mx-1';
      separator.textContent = '/';

      const link = document.createElement('a');
      link.href = origin.url;
      link.className = 'hover:underline hover:text-gray-900';
      link.textContent = origin.title;

      nav.appendChild(separator);
      nav.appendChild(link);

      // The empty state's "Browse all products" is a poorer offer than the
      // collection they actually came from.
      const emptyLink = this._empty && this._empty.querySelector('a');
      if (emptyLink) {
        emptyLink.href = origin.url;
        emptyLink.textContent = 'Back to ' + origin.title;
      }
    }

    // The query string is the source of truth so the URL stays shareable; the
    // store is only a fallback for someone landing on /pages/compare bare.
    _handles() {
      const param = new URLSearchParams(window.location.search).get('products');
      const fromUrl = param
        ? param
            .split(',')
            .map((h) => h.trim())
            .filter(Boolean)
        : [];
      const handles = fromUrl.length ? fromUrl : readSelection().map((item) => item.handle);
      return handles.slice(0, MAX);
    }

    // Same shape as updatePickupAvailability in sections/product.liquid. A handle
    // that no longer resolves drops its column instead of failing the page.
    _fetchColumn(handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '?sections=compare-column')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const html = data && data['compare-column'];
          if (!html) return null;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const column = doc.querySelector('[data-compare-column]');
          // An unknown handle does not 404 here — the Section Rendering API
          // answers 200 and renders the section against a nil product, which
          // would otherwise show up as a column of blanks. The handle the
          // section echoes back is empty in exactly that case.
          if (!column || !column.getAttribute('data-product-handle')) return null;
          return column;
        })
        .catch(() => null);
    }

    // Swaps the title link for a picker over the origin collection, so a column
    // can be changed without going back. The image keeps the link, since the
    // dropdown takes the only other route to the product page.
    _decorateHead(head, col) {
      const link = head.querySelector('a');
      if (!this._options || !link) return head;

      const media = head.querySelector('.compare-table__media');
      if (media && media.parentNode) {
        const mediaLink = document.createElement('a');
        mediaLink.href = link.getAttribute('href');
        mediaLink.className = 'compare-table__media-link';
        media.parentNode.insertBefore(mediaLink, media);
        mediaLink.appendChild(media);
      }

      const current = this._handleList[col];
      const select = document.createElement('select');
      select.className = 'compare-select';
      select.setAttribute('data-compare-swap', String(col));
      select.setAttribute('aria-label', 'Replace ' + link.textContent.trim());

      this._options.forEach((option) => {
        const el = document.createElement('option');
        el.value = option.handle;
        el.textContent = option.title;
        if (option.handle === current) el.selected = true;
        // Already in the comparison in another column — offering it would only
        // produce a duplicate.
        else if (this._handleList.indexOf(option.handle) !== -1) el.disabled = true;
        select.appendChild(el);
      });

      // A product reached some other way — a shared link, say — is not in the
      // collection, so add it or the select would show the wrong title.
      if (!this._options.some((option) => option.handle === current)) {
        const el = document.createElement('option');
        el.value = current;
        el.textContent = link.textContent.trim();
        el.selected = true;
        select.insertBefore(el, select.firstChild);
      }

      select.addEventListener('change', () => this._swap(col, select.value));

      // The select is laid transparently over a field we draw ourselves. A
      // select renders its own text and nothing can reach it — no fade at the
      // overflow edge, no second line — so the visible title is a span and the
      // control keeps only its behaviour, which is the part worth having: it
      // still opens the platform's native picker.
      const wrap = document.createElement('div');
      wrap.className = 'compare-select-wrap';
      const label = document.createElement('span');
      label.className = 'compare-select-label';
      // Decorative: the select underneath is what a screen reader announces.
      label.setAttribute('aria-hidden', 'true');
      label.textContent = select.options[select.selectedIndex].textContent;
      wrap.appendChild(label);
      wrap.appendChild(select);
      link.replaceWith(wrap);

      // The dropdown took the title's place as a link, so the way through to the
      // product needs saying explicitly. Styled by class rather than the utility
      // strings snippets/button.liquid emits — Tailwind only scans Liquid, so
      // classes written here would be purged.
      const view = document.createElement('a');
      view.href = link.getAttribute('href');
      view.className = 'compare-view-product';
      view.textContent = 'View';
      const icon = document.createElement('i');
      icon.className = 'ph ph-arrow-up-right';
      icon.setAttribute('aria-hidden', 'true');
      view.appendChild(icon);
      wrap.insertAdjacentElement('afterend', view);

      return head;
    }

    // byProduct=false — specs down the left, products across the top (desktop).
    // byProduct=true  — products down the left, specs across the top (mobile).
    // Same data either way; only which axis is which changes.
    _buildTable(columns, byProduct) {
      const wrapper = document.createElement('div');
      wrapper.className = 'metafield table-scroll';

      const table = document.createElement('table');
      table.className = 'compare-table text-sm' + (byProduct ? ' compare-table--by-product' : '');

      // Cloned, never moved: the stored columns have to survive being rebuilt
      // when the viewport crosses the breakpoint.
      const heads = columns.map((column, col) => {
        const head = column.querySelector('[data-compare-head]');
        return head ? this._decorateHead(head.cloneNode(true), col) : null;
      });
      const cellsPerColumn = columns.map((column) => [...column.querySelectorAll('[data-spec-cell]')]);

      // Every column renders every field in the same order, so field i is cell i
      // of every column — no key matching needed. Fields no product has a value
      // for are dropped, whichever axis they end up on.
      const keyAt = (i) => {
        const found = cellsPerColumn.find((cells) => cells[i]);
        return found ? found[i].getAttribute('data-spec-key') || '' : '';
      };
      const specs = [];
      this._labels.forEach((label, i) => {
        const anyValue = cellsPerColumn.some((cells) => cells[i] && !cells[i].hasAttribute('data-spec-empty'));
        if (!anyValue) return;
        // Transposed, the description would be a column several hundred words
        // deep, and a row is only as short as its tallest cell — one product's
        // description would be a screenful before the next. Left off entirely on
        // narrow screens; the linked product title still reaches the full text.
        if (byProduct && keyAt(i) === 'description') return;
        specs.push({ label: label, index: i });
      });

      const cellFor = (col, specIndex) => {
        const source = cellsPerColumn[col][specIndex];
        const td = document.createElement('td');
        if (source) {
          td.innerHTML = source.innerHTML;
          // Carried through so the description cell can opt out of the pre-line
          // whitespace the text metafields need.
          td.setAttribute('data-spec-key', source.getAttribute('data-spec-key') || '');
          if (source.hasAttribute('data-spec-empty')) td.setAttribute('data-spec-empty', '');
        } else {
          td.setAttribute('data-spec-empty', '');
        }
        return td;
      };

      const labelCell = (text) => {
        const th = document.createElement('th');
        th.scope = 'col';
        th.textContent = text;
        return th;
      };

      const corner = document.createElement('th');
      corner.scope = 'col';
      corner.textContent = this.getAttribute('data-corner-label') || '';

      const headRow = document.createElement('tr');
      headRow.appendChild(corner);
      const tbody = document.createElement('tbody');

      if (byProduct) {
        specs.forEach((spec) => headRow.appendChild(labelCell(spec.label)));

        columns.forEach((column, col) => {
          const tr = document.createElement('tr');
          const th = document.createElement('th');
          th.scope = 'row';
          if (heads[col]) th.appendChild(heads[col]);
          tr.appendChild(th);
          specs.forEach((spec) => tr.appendChild(cellFor(col, spec.index)));
          tbody.appendChild(tr);
        });
      } else {
        heads.forEach((head) => {
          const th = document.createElement('th');
          th.scope = 'col';
          if (head) th.appendChild(head);
          headRow.appendChild(th);
        });

        specs.forEach((spec) => {
          const tr = document.createElement('tr');
          const th = document.createElement('th');
          th.scope = 'row';
          th.textContent = spec.label;
          tr.appendChild(th);
          columns.forEach((column, col) => tr.appendChild(cellFor(col, spec.index)));
          tbody.appendChild(tr);
        });
      }

      const thead = document.createElement('thead');
      thead.appendChild(headRow);
      table.appendChild(thead);
      table.appendChild(tbody);
      wrapper.appendChild(table);
      return wrapper;
    }

    _showEmpty() {
      if (this._loading) this._loading.hidden = true;
      if (this._empty) this._empty.hidden = false;
    }
  }

  if (!customElements.get('product-compare-bar')) {
    customElements.define('product-compare-bar', ProductCompareBar);
  }
  if (!customElements.get('product-compare-table')) {
    customElements.define('product-compare-table', ProductCompareTable);
  }
})();
